
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tables } from '@/integrations/supabase/types';
import BlogEditForm from './blog/BlogEditForm';
import AIBlogGenerator from './blog/AIBlogGenerator';

type BlogPost = Tables<'blog_posts'>;

interface BlogEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  post: BlogPost | null;
}

const BlogEditDialog: React.FC<BlogEditDialogProps> = ({ isOpen, onClose, post }) => {
  const [title, setTitle] = useState(post?.title || '');
  const [content, setContent] = useState(post?.content || '');
  const [activeTab, setActiveTab] = useState('edit');

  // Reset state when dialog opens/closes or post changes
  useEffect(() => {
    if (isOpen) {
      setTitle(post?.title || '');
      setContent(post?.content || '');
    }
  }, [isOpen, post]);

  const handleContentGenerated = (generatedTitle: string, generatedContent: string) => {
    setTitle(generatedTitle);
    setContent(generatedContent);
    setActiveTab('edit'); // Switch back to edit tab after generation
  };

  // Create a modified post object with the updated title and content
  const modifiedPost = post ? {
    ...post,
    title,
    content
  } : null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-full max-h-[90vh] p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="text-xl font-semibold">
            {post ? 'Edit Blog Post' : 'Create New Blog Post'}
          </DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
          <div className="px-6 pt-4 border-b">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="edit">Edit Post</TabsTrigger>
              <TabsTrigger value="ai-generate">✨ AI Generate</TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="edit" className="m-0 h-full">
            <BlogEditForm 
              post={modifiedPost}
              onSuccess={onClose}
              initialTitle={title}
              initialContent={content}
            />
          </TabsContent>
          
          <TabsContent value="ai-generate" className="m-0 h-full">
            <div className="p-6">
              <AIBlogGenerator onContentGenerated={handleContentGenerated} />
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default BlogEditDialog;
